import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { CloseIcon } from "icons";
import { colors } from "theme/palette";

import Drawer from "components/Drawer";
import IconButton from "components/IconButton";
import SidepanelHeader from "components/SidepanelHeader";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";

import MemoESignatureProcessTrackingTable from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureProcessTrackingTable/index";
import { useMemoSignatureRoleWithMemoSignaturesList } from "entities/Memo/sdk";

export interface IMemoESignatureProcessTrackingDrawer {
  isOpen: boolean;
  onClose: () => void;
  memoName: string;
}

const MemoESignatureProcessTrackingDrawer = ({
  isOpen,
  onClose,
  memoName,
}: IMemoESignatureProcessTrackingDrawer) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const {
    data: memoSignatureRoles,
    isLoading,
    mutate: mutateRoles,
  } = useMemoSignatureRoleWithMemoSignaturesList({
    dealId,
    memoId,
    enabled: isOpen,
  });

  const onUpdate = useCallback(() => mutateRoles(), [mutateRoles]);

  const signaturesToBeCollected = useMemo(() => {
    return memoSignatureRoles?.roles.filter((role) =>
      role.memo_signature_fields?.some((field) => field.signatures?.length > 0)
    ).length;
  }, [memoSignatureRoles]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      sx={{
        width: "1014px",
        "& .MuiDrawer-paper": {
          width: "1014px",
          display: "flex",
        },
      }}
      onClose={onClose}
    >
      <Stack>
        <SidepanelHeader>
          <Stack>
            <Text variant="label" color={colors.blue100}>
              memos | {memoName}
            </Text>
            <Text variant="h1">Track e-sign process</Text>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ alignSelf: "flex-start" }}
          >
            <CloseIcon />
          </IconButton>
        </SidepanelHeader>
      </Stack>

      <Stack
        sx={{
          flex: 1,
          padding: 3,
        }}
        gap={2.5}
        overflow="hidden"
      >
        {!isLoading ? (
          <Text color={colors.gray60} variant="text3">
            {signaturesToBeCollected} signature
            {`${signaturesToBeCollected === 1 ? "" : "s"}`} to be collected
          </Text>
        ) : (
          <Skeleton variant="text" width="228px" height="21px" />
        )}

        <Stack flex={1} sx={{ overflowY: "auto" }}>
          {!_.isNil(memoSignatureRoles) && (
            <MemoESignatureProcessTrackingTable
              memoId={memoId}
              dealId={dealId}
              roles={memoSignatureRoles.roles}
              isLoading={isLoading}
              snapshot={memoSignatureRoles.snapshot}
              onClose={onClose}
              onUpdate={onUpdate}
            />
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default MemoESignatureProcessTrackingDrawer;
